import { create } from "zustand";
import { api } from "@/lib/api";
import type { InboxMessage, InboxFilter } from "@/types/inbox";

interface InboxState {
  messages: InboxMessage[];
  unreadCount: number;
  filter: InboxFilter;
  isLoading: boolean;
  error: string | null;
}

interface InboxActions {
  loadMessages(filter?: InboxFilter): Promise<void>;
  markRead(messageId: string): Promise<void>;
  reply(messageId: string, content: string): Promise<void>;
  setFilter(filter: InboxFilter): void;
}

export const useInboxStore = create<InboxState & InboxActions>((set, get) => ({
  messages: [],
  unreadCount: 0,
  filter: {},
  isLoading: false,
  error: null,

  async loadMessages(filter?: InboxFilter) {
    const activeFilter = filter ?? get().filter;
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (activeFilter.channelType)
        params.set("channelType", activeFilter.channelType);
      if (activeFilter.urgency) params.set("urgency", activeFilter.urgency);
      if (activeFilter.isRead !== undefined)
        params.set("isRead", String(activeFilter.isRead));
      if (activeFilter.search) params.set("search", activeFilter.search);

      const query = params.toString();
      const path = `/api/dashboard/inbox${query ? `?${query}` : ""}`;
      const messages = await api.get<InboxMessage[]>(path);
      const unreadCount = messages.filter((m) => !m.isRead).length;
      set({ messages, unreadCount, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error:
          err instanceof Error ? err.message : "Failed to load messages",
      });
    }
  },

  async markRead(messageId: string) {
    try {
      await api.patch(`/api/dashboard/inbox/${messageId}/read`);
      set((state) => {
        const messages = state.messages.map((m) =>
          m.id === messageId ? { ...m, isRead: true } : m,
        );
        const unreadCount = messages.filter((m) => !m.isRead).length;
        return { messages, unreadCount };
      });
    } catch (err) {
      set({
        error:
          err instanceof Error
            ? err.message
            : "Failed to mark message as read",
      });
    }
  },

  async reply(messageId: string, content: string) {
    try {
      await api.post(`/api/dashboard/inbox/${messageId}/reply`, { content });
    } catch (err) {
      set({
        error:
          err instanceof Error ? err.message : "Failed to send reply",
      });
    }
  },

  setFilter(filter: InboxFilter) {
    set({ filter });
  },
}));
